import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2 } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ButtonWrapper,
  ParagraphsRenderer,
  SearchMoreLikeThis,
  Tags,
  VariableRatiosPicture,
} from '../../../../components';
import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import useLayoutVariables from '../../../../hooks/useLayoutVariables';
import type { NodeProject } from '../../../../types/uncinc_profile/nodes';

import './style.scss';

interface NodeProjectPageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeProject>;
}

const NodeProjectPage: React.FC<NodeProjectPageProps> = ({ entity, className }) => {
  const { t } = useTranslation();
  const {
    heroImageBreakpointAspectRatios,
    breadcrumbsColClasses,
    heroTextColClasses,
    heroImageColClasses,
  } = useLayoutVariables('project');

  const node = entity.toJS();
  const nid = node.nid[0].value;
  const langcode = node.langcode[0].value;
  const title = node.title[0].value;
  const body = node.body[0]?.processed;
  const heroImage = node.field_media_item[0];
  const link = node.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const projectTypes = node.field_project_types;
  const projectStatuses = node.field_project_status;
  const projectStatus = projectStatuses[0]?.entity.name[0].value;
  const partner = node.field_partner[0]?.entity;
  const partnerTitle = partner?.title[0].value;

  const classes = classNames(
    'Node',
    'NodeProjectPage',
    className,
    projectTypes.map((projectType) => `NodeProjectPage--type--${String(projectType.entity.name[0].value)}`),
    {
      [`NodeProjectPage--status--${String(projectStatus)}`]: projectStatus !== undefined,
    },
  );

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeProjectPage__hero">
        <Container>
          <div className="row">
            <div className={breadcrumbsColClasses}>
              <BreadcrumbsV2 />
            </div>
          </div>
          <div className="row">
            <div className={heroTextColClasses}>
              <div className="NodeProjectPage__hero__text-wrapper">
                <div className="NodeProjectPage__hero__text">
                  {(projectTypes.length > 0 || projectStatuses.length > 0) && (
                    <div className="NodeProjectPage__tags">
                      {projectTypes.length > 0 && (
                        <Tags items={taxonomyTagEntityMapper(projectTypes)} />
                      )}
                      {projectStatuses.length > 0 && (
                        <Tags items={taxonomyTagEntityMapper(projectStatuses)} />
                      )}
                    </div>
                  )}

                  <h1 className="NodeProjectPage__title" itemProp="title">
                    {title}
                  </h1>

                  {body && (
                    <WysiwygField
                      className="NodeProjectPage__body body--large"
                      property="schema:text"
                      html={body}
                    />
                  )}

                  {partnerTitle && (
                    <div className="NodeProjectPage__partner">
                      <p>{t('content_types.project.partner_text', { partnerTitle })}</p>
                    </div>
                  )}

                  {linkUrl && (
                    <div className="NodeProjectPage__link">
                      <ButtonWrapper href={linkUrl}>
                        {linkTitle || t('general.read_more')}
                      </ButtonWrapper>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {heroImage && (
              <div className={heroImageColClasses}>
                <div className="NodeProjectPage__hero__image-wrapper">
                  <div className="NodeProjectPage__hero__image">
                    <VariableRatiosPicture
                      image={heroImage}
                      aspectRatios={heroImageBreakpointAspectRatios}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <ParagraphsRenderer className="NodeProjectPage__paragraphs" paragraphs={entity.get('field_paragraphs')} />

      <div className="NodeProjectPage__related-content">
        <Container>
          <div className="row">
            <div className="col-12 col-xl-10 offset-xl-1">
              <SearchMoreLikeThis
                entityId={nid}
                langcode={langcode}
                contentTypes={['project']}
                title={t('content_types.project.related_content')}
              />
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeProjectPage;
